import * as React from 'react'
import { useTranslation } from 'react-i18next'
import type { BadgeVariantProps, ButtonProps } from '@components'
import type { IconProps } from '@components'
import i18n from '@core/translations'
import { useTheme } from '@core/utils'

const useContentConfig = () => {
    const { t } = useTranslation()
    const theme = useTheme().colors

    const contents = React.useMemo(() => {
        const data = {
            locale: i18n.getLocale(),
            historyItem: {
                types: [
                    {
                        key: 'refund',
                        icon: 'refund' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund'),
                        amountColor: theme.semantic.content.error,
                        pointChipType: 'brand',
                    },
                    {
                        key: 'payment_refund',
                        icon: 'refund' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund'),
                        amountColor: theme.semantic.content.error,
                        pointBadgeType: 'brand' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'deposit_refund',
                        icon: 'refund' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund'),
                        amountColor: theme.semantic.content.error,
                        pointBadgeType: 'brand' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'cash_deposit_refund',
                        icon: 'refund' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund'),
                        amountColor: theme.semantic.content.error,
                        pointBadgeType: 'brand' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'top-up',
                        icon: 'top-up' as IconProps['name'],
                        title: t('content_config.HistoryItem.topUp'),
                        amountColor: theme.semantic.content.success,
                        pointBadgeType:
                            'yellow' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'capture',
                        icon: 'fullscreen' as IconProps['name'],
                        title: t('content_config.HistoryItem.capture'),
                        amountColor: theme.semantic.content.ink,
                        pointBadgeType:
                            'yellow' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'approve',
                        icon: 'warning' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund_approval'),
                        amountColor: theme.semantic.content.ink,
                        pointBadgeType: 'brand' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'close',
                        icon: 'warning' as IconProps['name'],
                        title: t('content_config.HistoryItem.refund_reject'),
                        amountColor: theme.semantic.content.error,
                        pointBadgeType: 'brand' as BadgeVariantProps['variant'],
                    },
                    {
                        key: 'package_usage',
                        icon: 'subscription' as IconProps['name'],
                        title: t('content_config.HistoryItem.package_usage'),
                        amountColor: theme.semantic.content.ink,
                    },
                    {
                        key: 'subscription_usage',
                        icon: 'subscription' as IconProps['name'],
                        title: t(
                            'content_config.HistoryItem.subscription_usage',
                        ),
                        amountColor: theme.semantic.content.ink,
                    },
                    {
                        key: 'invoice',
                        icon: 'invoice' as IconProps['name'],
                        title: t('content_config.HistoryItem.invoice'),
                        amountColor: theme.semantic.content.ink,
                    },
                    {
                        key: 'entry_control_approved',
                        icon: 'security' as IconProps['name'],
                        title: t(
                            'content_config.HistoryItem.entry_control_approved',
                        ),
                    },
                    {
                        key: 'entry_control_denied',
                        icon: 'warning' as IconProps['name'],
                        title: t(
                            'content_config.HistoryItem.entry_control_denied',
                        ),
                    },
                ],
            },
            campaignItem: {
                detailsButtonText: t(
                    'content_config.CampaignItem.details_button_text',
                ),
                disabledButtonText: t(
                    'content_config.CampaignItem.disabled_button_text',
                ),
                remainingTime: t('content_config.CampaignItem.remaining_time'),
                day: t('content_config.CampaignItem.day'),
                pointSuffix: t('content_config.CampaignItem.point_suffix'),
                percentSuffix: t('content_config.CampaignItem.percent_suffix'),
            },
            walletCardInstruction: {
                statuses: [
                    {
                        key: 'pending',
                        label: t('@deposit.TopUpInstruction.status_pending'),
                        actionButtonVariant:
                            'primary' as ButtonProps['variant'],
                    },
                    {
                        key: 'active',
                        label: t('@deposit.TopUpInstruction.status_active'),
                        actionButtonVariant:
                            'primary' as ButtonProps['variant'],
                    },
                    {
                        key: 'suspended',
                        label: t('@deposit.TopUpInstruction.status_suspended'),
                        actionButtonVariant:
                            'secondary' as ButtonProps['variant'],
                    },
                    {
                        key: 'cancelled',
                        label: t('@deposit.TopUpInstruction.status_cancelled'),
                        actionButtonVariant: 'ink' as ButtonProps['variant'],
                    },
                    {
                        key: 'expired',
                        label: t('@deposit.TopUpInstruction.status_expired'),
                        actionButtonVariant: 'ink' as ButtonProps['variant'],
                    },
                ],
            },
            rewardChart: {
                tableTypeTitle: t(
                    'content_config.RewardChart.table_type_title',
                ),
                progressTypeTitle: t(
                    'content_config.RewardChart.progress_type_title',
                ),
                progressTypeDescription: t(
                    'content_config.RewardChart.progress_type_description',
                ),
                tableTypeDescription: t(
                    'content_config.RewardChart.table_type_description',
                ),
            },
        }
        return data
    }, [t, theme])

    return contents
}

export default useContentConfig
